import { Button, cn } from "@qn/ui";
import { Mic, MicOff, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { openMic } from "../lib/voice";

type Phase = "connecting" | "listening" | "hearing" | "thinking" | "speaking" | "error";
const IDLE_MS = 120_000; // hang up after two silent minutes: Realtime is billed per minute

/** Post the browser's offer to OpenAI; try the current endpoint first, then the older one. */
async function exchangeSdp(secret: string, model: string, offer: string): Promise<string> {
  const headers = { authorization: `Bearer ${secret}`, "content-type": "application/sdp" };
  for (const url of [
    "https://api.openai.com/v1/realtime/calls",
    `https://api.openai.com/v1/realtime?model=${encodeURIComponent(model)}`,
  ]) {
    const r = await fetch(url, { method: "POST", body: offer, headers });
    if (r.ok) return r.text();
    if (r.status !== 404 && r.status !== 405) throw new Error(`OpenAI Realtime (${r.status})`);
  }
  throw new Error("OpenAI Realtime endpoint not found");
}

/**
 * Full-duplex live voice through OpenAI Realtime over WebRTC: you can talk over the assistant at any moment.
 * The short-lived key is minted by our server from the user's own OpenAI key; audio goes straight to OpenAI (paid, per minute).
 */
export function RealtimeVoice({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [phase, setPhase] = useState<Phase>("connecting");
  const [heard, setHeard] = useState("");
  const [said, setSaid] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const mic = useRef<MediaStream | null>(null);
  const idle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // biome-ignore lint/correctness/useExhaustiveDependencies: one session per mount
  useEffect(() => {
    let closed = false;
    const pc = new RTCPeerConnection();
    const audio = new Audio();
    audio.autoplay = true;
    const bump = () => {
      clearTimeout(idle.current);
      idle.current = setTimeout(() => closeRef.current(), IDLE_MS);
    };
    pc.ontrack = (e) => {
      audio.srcObject = e.streams[0] ?? null;
    };
    const onEvent = (raw: string) => {
      bump();
      let e: { type?: string; transcript?: string; delta?: string; error?: { message?: string } };
      try {
        e = JSON.parse(raw);
      } catch {
        return;
      }
      switch (e.type) {
        case "input_audio_buffer.speech_started":
          setPhase("hearing"); // also interrupts the assistant: the server cuts its audio
          break;
        case "input_audio_buffer.speech_stopped":
          setPhase("thinking");
          break;
        case "conversation.item.input_audio_transcription.completed":
          setHeard(e.transcript ?? "");
          break;
        case "response.created":
          setSaid("");
          setPhase("thinking");
          break;
        case "response.output_audio_transcript.delta":
          setSaid((s) => s + (e.delta ?? ""));
          setPhase("speaking");
          break;
        case "response.output_audio_transcript.done":
          if (e.transcript) setSaid(e.transcript);
          break;
        case "response.done":
          setPhase("listening");
          break;
        case "error":
          setError(e.error?.message ?? "Realtime error");
          break;
      }
    };
    (async () => {
      try {
        const { secret, model } = await api<{ secret: string; model: string }>(
          "/voice/realtime/session",
          { method: "POST", body: {} },
        );
        const ms = await openMic();
        if (closed) {
          for (const x of ms.getTracks()) x.stop();
          return;
        }
        mic.current = ms;
        for (const track of ms.getAudioTracks()) pc.addTrack(track, ms);
        const dc = pc.createDataChannel("oai-events");
        dc.onmessage = (m) => onEvent(String(m.data));
        dc.onopen = () => {
          setPhase("listening");
          bump();
        };
        await pc.setLocalDescription(await pc.createOffer());
        const answer = await exchangeSdp(secret, model, pc.localDescription?.sdp ?? "");
        if (closed) return;
        await pc.setRemoteDescription({ type: "answer", sdp: answer });
      } catch (e) {
        if (!closed) {
          setError(e instanceof Error ? e.message : String(e));
          setPhase("error");
        }
      }
    })();
    return () => {
      closed = true;
      clearTimeout(idle.current);
      pc.close();
      audio.srcObject = null;
      for (const x of mic.current?.getTracks() ?? []) x.stop();
    };
  }, []);

  const toggleMute = () => {
    const next = !muted;
    for (const tr of mic.current?.getAudioTracks() ?? []) tr.enabled = !next;
    setMuted(next);
  };
  const label: Record<Phase, string> = {
    connecting: t("voice.rt.connecting"),
    listening: t("voice.listening"),
    hearing: t("voice.hearing"),
    thinking: t("voice.thinking"),
    speaking: t("voice.speaking"),
    error: t("voice.rt.error"),
  };
  const active = phase === "hearing" || phase === "speaking";
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("voice.title")}
      className="fixed inset-0 z-[70] flex flex-col items-center justify-between bg-[var(--bg)]/95 p-6 backdrop-blur-xl"
    >
      <div className="flex w-full max-w-md items-center justify-between">
        <span className="text-sm font-medium text-[var(--fg-muted)]">
          {t("voice.title")} · <span className="text-[var(--danger)]">{t("voice.rt.badge")}</span>
        </span>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label={t("artifact.close")}>
          <X className="size-5" />
        </Button>
      </div>
      <div className="flex flex-col items-center gap-8">
        <div className="relative grid size-56 place-items-center">
          <div
            aria-hidden
            className={cn(
              "absolute inset-6 rounded-full bg-gradient-to-br from-[var(--accent)] to-[var(--accent-2)] transition-transform duration-200",
              phase === "connecting" || phase === "thinking" ? "animate-pulse" : "",
            )}
            style={{ transform: `scale(${active ? 1.12 : 1})` }}
          />
          {muted ? (
            <MicOff className="relative size-10 text-[var(--accent-fg)]" />
          ) : (
            <Mic className="relative size-10 text-[var(--accent-fg)]" />
          )}
        </div>
        <div className="text-center">
          <div role="status" aria-live="polite" className="font-display text-2xl font-semibold">
            {muted ? t("voice.muted") : label[phase]}
          </div>
          {heard && (
            <p className="mx-auto mt-3 max-w-md text-sm text-[var(--fg-muted)]" dir="auto">
              “{heard}”
            </p>
          )}
          {said && (
            <p
              className="mx-auto mt-4 max-h-40 max-w-md overflow-y-auto text-start text-sm"
              dir="auto"
            >
              {said}
            </p>
          )}
          {error && (
            <p
              role="alert"
              className="mx-auto mt-3 max-w-md text-sm text-[var(--danger)]"
              dir="auto"
            >
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="outline" size="lg" onClick={toggleMute}>
          {muted ? <Mic className="size-4" /> : <MicOff className="size-4" />}{" "}
          {muted ? t("voice.unmute") : t("voice.mute")}
        </Button>
        <Button variant="danger" size="lg" onClick={onClose}>
          {t("voice.end")}
        </Button>
      </div>
    </div>
  );
}
